import { makeStyles, ThemeProvider } from '@ui';
import { Card, CardActions, CardContent, Tooltip } from '@ui/components';
import { InfoOutlinedIcon } from '@ui/icons';
import React from 'react';
import { formatInCurrency, Typography } from '../common';
import { PayInvoicesButton } from './PayInvoicesButton';
import { BillingTableItem } from './types';
import { useMetricsQuery } from './useMetricsQuery';
import { useUnpaidBillingItems } from './useUnpaidBillingItems';

type OutstandingBalanceProps = {
  options: Intl.NumberFormatOptions;
  invoices: BillingTableItem[];
};

const useStyles = makeStyles(({ palette }) => ({
  card: { width: '444px', minHeight: '163px' },
  transparentButton: { backgroundColor: 'transparent' },
  boldText: { fontWeight: 700 },
  successLabel: { color: palette.success.main },
}));

export const OutstandingBalance: React.FC<OutstandingBalanceProps> = ({ invoices, options }) => {
  const { unpaidInvoices } = useUnpaidBillingItems(invoices);
  const { outstandingBalance } = useMetricsQuery();
  const classes = useStyles();

  return (
    <ThemeProvider>
      <Card classes={{ root: classes.card }}>
        <CardContent>
          <Typography variant='h3' color='textPrimary' gutterBottom>
            Outstanding Balance <InfoTooltip />
          </Typography>

          <Typography variant='body2' color='textPrimary'>
            (Including applied Credit Memos)
          </Typography>
        </CardContent>

        <CardActions disableSpacing>
          {outstandingBalance ? (
            <>
              <Typography variant='body1' color='textPrimary' classes={{ root: classes.boldText }}>
                {options.currency} {formatInCurrency(outstandingBalance, options)}
              </Typography>

              {!!unpaidInvoices.length && (
                <PayInvoicesButton
                  billingItems={unpaidInvoices}
                  variant='outlined'
                  classes={{ root: classes.transparentButton }}
                >
                  Pay All
                </PayInvoicesButton>
              )}
            </>
          ) : (
            <Typography classes={{ root: classes.successLabel }}>
              Your account balance is paid.
            </Typography>
          )}
        </CardActions>
      </Card>
    </ThemeProvider>
  );
};

export const InfoTooltip = () => {
  const title = `
    Outstanding Balance is the sum of all the open invoices and debit memos.
    Please note, this amount does not include any unapplied credits.
    Check the box on the right for the unapplied credits.
  `;

  return (
    <Tooltip title={title} placement='right-start'>
      <InfoOutlinedIcon color='action' />
    </Tooltip>
  );
};
